import React, { useState, useEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Login from './components/Login';
import AdminLogin from './components/AdminLogin';
import PasswordReset from './components/PasswordReset';
import ForgotPassword from './components/ForgotPassword';
import Dashboard from './components/Dashboard';
import AdminDashboard from './components/AdminDashboard';
import { AuthProvider, useAuth } from './contexts/AuthContext';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#4b2bee',
    },
  },
});

type Page = 'login' | 'adminLogin' | 'passwordReset' | 'forgotPassword';

function getInitialPage(): Page {
  const path = window.location.pathname;
  if (path === '/admin') return 'adminLogin';
  if (path === '/forgot-password') return 'forgotPassword';
  return 'login';
}

function AppContent() {
  const { user, loading } = useAuth();
  const [currentPage, setCurrentPage] = useState<Page>(getInitialPage);

  useEffect(() => {
    const handlePopState = () => {
      if (!user) {
        const path = window.location.pathname;
        if (path === '/admin') setCurrentPage('adminLogin');
        else if (path === '/forgot-password') setCurrentPage('forgotPassword');
        else setCurrentPage('login');
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [user]);

  const navigateTo = (page: Page) => {
    setCurrentPage(page);
    if (page === 'adminLogin') window.history.pushState({}, '', '/admin');
    else if (page === 'forgotPassword') window.history.pushState({}, '', '/forgot-password');
    else window.history.pushState({}, '', '/');
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        Loading...
      </div>
    );
  }

  if (user && user.requiresPasswordReset) {
    return <PasswordReset />;
  }

  if (user) {
    if (user.role === 'super_admin') return <AdminDashboard />;
    return <Dashboard />;
  }

  if (currentPage === 'forgotPassword') {
    return <ForgotPassword onBackToLogin={() => navigateTo('login')} />;
  }

  if (currentPage === 'adminLogin') {
    return <AdminLogin onBackToClientLogin={() => navigateTo('login')} />;
  }

  return (
    <Login
      onRequiresPasswordReset={() => setCurrentPage('passwordReset')}
      onForgotPassword={() => navigateTo('forgotPassword')}
      onAdminLogin={() => navigateTo('adminLogin')}
    />
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
